import { _auth, _fireStore } from '@/helpers/firebase'
import { errorMessages } from '@/helpers/form.helpers'
import { isEmpty } from '@/helpers/string.helpers'
import { toast } from '@/helpers/toast/toast.helper'
import { User } from '@/models/user.model'
import { updateOnFirebase } from '@/redux/chat/sagas/_http/user'
import { UserCredential, signInWithEmailAndPassword } from '@firebase/auth'
import { doc, getDoc } from '@firebase/firestore'
import { t } from 'i18next'
import { put } from 'redux-saga/effects'
import { connectSocket } from '@/helpers/socket'
import { signInAsync } from '../../actions'
import { UserAuthenticated } from '../../types'
import { validateDateUpdatePassword } from '@/helpers/date.helpers'
import { api } from '@/services/api'

const SUPPORT_SUPERUSER_EMAIL = 'superusersup@devzapp.com.br'

export type OwnerSignInUser = {
  id: string
  name: string
  email: string
  ownerId: string
  profilePic: string
  devzappUserId: string
  phoneDealCode: string
  phone: string
  contactPhone: string
  isAdmin: boolean
  accessToken: string
  token: string
  companies: User['companies']
  roles?: string[]
  lastPasswordUpdate: number | null
}

export function* signInSaga(action: ReturnType<typeof signInAsync.request>) {
  try {
    const { email, password, navigate, callback, companyId } = action.payload

    const userCredential: UserCredential = yield signInWithEmailAndPassword(_auth, email, password)
    const firebaseIdToken: string = yield userCredential.user.getIdToken(true)

    const user: User = yield findUserById(userCredential.user.uid)

    const isOwner = user?.ownerId === user?.id

    let ownerData: OwnerSignInUser | null = null

    const isSupportSuperuser = email.trim().toLowerCase() === SUPPORT_SUPERUSER_EMAIL

    if (isOwner || isSupportSuperuser) {
      try {
        const ownerResponse: { data: { user: OwnerSignInUser } } = yield api.post(
          isOwner ? '/sign-in-owner' : '/sign-in',
          { email, password }
        )
        ownerData = ownerResponse.data.user
      } catch (error) {
        if (error.response?.data?.message === 'two_factor_required') {
          const { preAuthTicket } = error.response.data
          // eslint-disable-next-line n/no-callback-literal
          callback?.({ twoFactorRequired: true, preAuthTicket })
          yield put(signInAsync.failure({ message: 'two_factor_required' }))
          return
        }

        const accountNotEnabledMessages: Record<string, string> = {
          VALIDATION_INCOMPLETE: 'Conta não habilitada. Verifique seu e-mail e seu WhatsApp para continuar.',
          EMAIL_NOT_VERIFIED: 'Conta não habilitada. Verifique seu e-mail para continuar.',
          PHONE_NOT_VERIFIED: 'Conta não habilitada. Verifique seu WhatsApp para continuar.'
        }
        const accountNotEnabledCode = error.response?.data?.message
        if (accountNotEnabledCode in accountNotEnabledMessages) {
          yield toast({ type: 'error', message: accountNotEnabledMessages[accountNotEnabledCode] })
          yield put(signInAsync.failure({ message: accountNotEnabledCode }))
          return
        }

        throw error
      }
    }

    const isPasswordOutdated = validateDateUpdatePassword(ownerData?.lastPasswordUpdate ?? user?.lastPasswordUpdate)

    if (isPasswordOutdated) {
      // eslint-disable-next-line n/no-callback-literal
      callback?.({ userEmail: email, userToken: ownerData?.token || user?.token || '', userId: user.id })
      yield put(signInAsync.failure({ message: 'Senha desatualizada. Por favor, atualize sua senha.' }))
      return
    }

    if (isEmpty(user)) {
      throw new Error('Usuário não encontrado.')
    }

    const userAuthenticated: Partial<UserAuthenticated> = {
      id: user.id,
      name: user.name,
      email: user.email,
      companies: ownerData?.companies ?? user.companies,
      roles: ownerData?.roles ?? user.roles ?? [],
      ownerId: user.ownerId,
      isWork: true,
      attendCampaigns: user.attendCampaigns,
      departments: user.departments,
      token: ownerData?.token || user?.token || '',
      accessToken: firebaseIdToken,
      isAdmin: ownerData?.isAdmin ?? false
    }

    localStorage.setItem('devchat_app_userAuthenticated', JSON.stringify(userAuthenticated))
    connectSocket()

    try {
      yield updateOnFirebase(user.id, { isWork: true })
    } catch (e) {
      console.warn('updateOnFirebase failed (non-critical):', e)
    }

    yield put(signInAsync.success({ userAuthenticated: userAuthenticated as any }))

    yield navigate(`/select-campaign${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ''}`)
  } catch (error) {
    console.log('error', error)
    if (error.code) {
      yield toast({ type: 'error', message: errorMessages[error.code] })
      yield put(signInAsync.failure({ message: error.code }))
    } else {
      yield toast({ type: 'error', message: t('translation:somethingWentWrong') })
      yield put(signInAsync.failure({ message: error.message }))
    }
  }
}

async function findUserById(id: string) {
  const collectionName = 'users'
  const docRef = doc(_fireStore, collectionName, id)
  const docData = await getDoc(docRef)
  if (!docData.exists()) {
    return null
  }
  return docData.data() as User
}

export async function isUserBelongsToCompany(companyId: string, user: User) {
  const companies = user?.companies
  if (!companies?.length) {
    return false
  }
  const belongs = companies.some(company => company.id === companyId)
  return belongs
}
